import { useTheme } from '../App';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Shield, Code2, Lock, Users, FileCheck, Award } from 'lucide-react';

const strings = {
  en: {
    heroTitle: 'We are myAct',
    heroSubtitle: 'A company specialized in digital technology and user rights protection',
    heroDesc: 'We help individuals and organizations navigate the complex world of digital platform appeals under the EU Digital Services Act.',
    missionTitle: 'Our Mission',
    missionDesc: 'myAct was founded with a singular purpose: to empower users who have been unfairly treated by major technology platforms. Whether your account was suspended, your content was removed, or your rights were violated, we provide a structured, professional path to appeal and resolution.',
    missionDesc2: 'We leverage the European Union\'s Digital Services Act (DSA) to hold platforms accountable, giving every user — regardless of technical expertise — a voice against unjust automated decisions.',
    valuesTitle: 'Our Values',
    v1Title: 'Digital Rights',
    v1Desc: 'Every user has the right to fair treatment online. We defend those rights through the DSA framework and legal expertise.',
    v2Title: 'Technology',
    v2Desc: 'Our platform uses cutting-edge technology to streamline the appeal process, making it accessible, fast, and effective.',
    v3Title: 'Privacy',
    v3Desc: 'Your data is yours. We handle all information with the highest standards of privacy and GDPR compliance.',
    teamTitle: 'Our Team',
    teamDesc: 'myAct is powered by a team of legal experts, engineers, and digital rights advocates working together to protect your online presence.',
    teamPlaceholder: 'Team profiles coming soon',
    statsTitle: 'By the Numbers',
    s1: 'Appeals Filed',
    s2: 'Platforms Covered',
    s3: 'Success Rate',
  },
  ar: {
    heroTitle: 'نحن myAct',
    heroSubtitle: 'شركة متخصصة بالتكنولوجيا الرقمية وحماية حقوق المستخدم',
    heroDesc: 'نساعد الأفراد والمؤسسات على التعامل مع منصات التكنولوجيا الكبرى وتقديم الطعون بموجب قانون الخدمات الرقمية الأوروبي.',
    missionTitle: 'مهمتنا',
    missionDesc: 'تأسست myAct بهدف واحد: تمكين المستخدمين الذين عوملوا بشكل غير عادل من قِبَل منصات التكنولوجيا الكبرى. سواء تم تعليق حسابك أو حذف محتواك أو انتهاك حقوقك، نوفر مساراً منظماً واحترافياً للطعن والحل.',
    missionDesc2: 'نستخدم قانون الخدمات الرقمية (DSA) للاتحاد الأوروبي لمحاسبة المنصات، مما يمنح كل مستخدم — بغض النظر عن خبرته التقنية — صوتاً في مواجهة القرارات الآلية غير العادلة.',
    valuesTitle: 'قيمنا',
    v1Title: 'الحقوق الرقمية',
    v1Desc: 'لكل مستخدم الحق في المعاملة العادلة عبر الإنترنت. ندافع عن هذه الحقوق من خلال إطار DSA والخبرة القانونية.',
    v2Title: 'التكنولوجيا',
    v2Desc: 'تستخدم منصتنا أحدث التقنيات لتبسيط عملية الاستئناف، مما يجعلها في متناول الجميع وسريعة وفعالة.',
    v3Title: 'الخصوصية',
    v3Desc: 'بياناتك ملكك. نتعامل مع جميع المعلومات بأعلى معايير الخصوصية والامتثال للائحة GDPR.',
    teamTitle: 'فريقنا',
    teamDesc: 'تعمل myAct بفريق من خبراء قانونيين ومهندسين ومدافعين عن الحقوق الرقمية يعملون معاً لحماية وجودك على الإنترنت.',
    teamPlaceholder: 'ملفات تعريف الفريق قريباً',
    statsTitle: 'بالأرقام',
    s1: 'استئناف مقدّم',
    s2: 'منصة مغطاة',
    s3: 'نسبة النجاح',
  },
  de: {
    heroTitle: 'Wir sind myAct',
    heroSubtitle: 'Ein auf digitale Technologie und Nutzerrechtsschutz spezialisiertes Unternehmen',
    heroDesc: 'Wir helfen Einzelpersonen und Organisationen, die komplexe Welt der digitalen Plattform-Beschwerden nach dem EU Digital Services Act zu navigieren.',
    missionTitle: 'Unsere Mission',
    missionDesc: 'myAct wurde mit einem einzigen Zweck gegründet: Nutzer zu stärken, die von großen Technologieplattformen ungerecht behandelt wurden. Ob Ihr Konto gesperrt, Ihr Inhalt entfernt oder Ihre Rechte verletzt wurden – wir bieten einen strukturierten, professionellen Weg zur Berufung und Lösung.',
    missionDesc2: 'Wir nutzen das Digitale-Dienste-Gesetz (DSA) der Europäischen Union, um Plattformen zur Rechenschaft zu ziehen, und geben jedem Nutzer – unabhängig von technischen Kenntnissen – eine Stimme gegen unfaire automatisierte Entscheidungen.',
    valuesTitle: 'Unsere Werte',
    v1Title: 'Digitale Rechte',
    v1Desc: 'Jeder Nutzer hat das Recht auf faire Behandlung online. Wir verteidigen diese Rechte durch den DSA-Rahmen und juristische Expertise.',
    v2Title: 'Technologie',
    v2Desc: 'Unsere Plattform nutzt modernste Technologie, um den Berufungsprozess zu optimieren und ihn zugänglich, schnell und effektiv zu machen.',
    v3Title: 'Datenschutz',
    v3Desc: 'Ihre Daten gehören Ihnen. Wir behandeln alle Informationen nach höchsten Datenschutzstandards und DSGVO-Konformität.',
    teamTitle: 'Unser Team',
    teamDesc: 'myAct wird von einem Team aus Rechtsexperten, Ingenieuren und Digitalrechts-Aktivisten betrieben, die gemeinsam Ihre Online-Präsenz schützen.',
    teamPlaceholder: 'Team-Profile demnächst verfügbar',
    statsTitle: 'In Zahlen',
    s1: 'Eingereichte Beschwerden',
    s2: 'Abgedeckte Plattformen',
    s3: 'Erfolgsquote',
  },
};

export default function AboutPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { i18n } = useTranslation();
  const lang = ['en','ar','de'].includes(i18n.language) ? i18n.language : 'en';
  const s = strings[lang];

  const card = isDark
    ? 'bg-[#0A0A0C]/60 backdrop-blur-xl border border-white/[0.05]'
    : 'bg-white border border-gray-200 shadow-sm';
  const textP = isDark ? 'text-white' : 'text-gray-900';
  const textS = isDark ? 'text-white/60' : 'text-gray-600';
  const textM = isDark ? 'text-white/40' : 'text-gray-400';

  const valueCards = [
    { icon: Shield, title: s.v1Title, desc: s.v1Desc, color: 'text-blue-500', bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50' },
    { icon: Code2, title: s.v2Title, desc: s.v2Desc, color: 'text-purple-500', bg: isDark ? 'bg-purple-500/10' : 'bg-purple-50' },
    { icon: Lock, title: s.v3Title, desc: s.v3Desc, color: 'text-emerald-500', bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50' },
  ];

  const stats = [
    { icon: FileCheck, value: '10,000+', label: s.s1, color: 'text-blue-500', bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50' },
    { icon: Users, value: '50+', label: s.s2, color: 'text-purple-500', bg: isDark ? 'bg-purple-500/10' : 'bg-purple-50' },
    { icon: Award, value: '98%', label: s.s3, color: 'text-emerald-500', bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50' },
  ];

  return (
    <div className={`min-h-screen transition-colors ${isDark ? '' : 'bg-[#F8FAFC]'}`}>
      {/* Hero */}
      <section className="relative overflow-hidden py-24 px-6">
        {isDark && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl" />
          </div>
        )}
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-6 border ${isDark ? 'bg-blue-500/10 border-blue-500/20 text-blue-400' : 'bg-blue-50 border-blue-200 text-blue-600'}`}>
              <Shield className="w-4 h-4" /> myAct — Digital Rights Platform
            </div>
            <h1 className={`text-4xl md:text-6xl font-bold tracking-tight mb-6 ${textP}`} style={{ fontFamily: 'Outfit' }}>
              {s.heroTitle}
            </h1>
            <p className={`text-xl md:text-2xl font-medium mb-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
              {s.heroSubtitle}
            </p>
            <p className={`text-lg max-w-2xl mx-auto leading-relaxed ${textS}`}>
              {s.heroDesc}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className={`text-2xl font-bold text-center mb-10 ${textP}`} style={{ fontFamily: 'Outfit' }}>
            {s.statsTitle}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((st, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className={`${card} rounded-2xl p-8 text-center`}>
                <div className={`w-14 h-14 ${st.bg} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <st.icon className={`w-7 h-7 ${st.color}`} />
                </div>
                <div className={`text-4xl font-bold mb-2 ${textP}`} style={{ fontFamily: 'Outfit' }}>{st.value}</div>
                <div className={`text-sm ${textS}`}>{st.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className={`${card} rounded-3xl p-10`}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className={`text-3xl font-bold mb-6 ${textP}`} style={{ fontFamily: 'Outfit' }}>{s.missionTitle}</h2>
              <p className={`text-lg leading-relaxed mb-4 ${textS}`}>{s.missionDesc}</p>
              <p className={`text-lg leading-relaxed ${textS}`}>{s.missionDesc2}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className={`text-2xl font-bold text-center mb-10 ${textP}`} style={{ fontFamily: 'Outfit' }}>
            {s.valuesTitle}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {valueCards.map((vc, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className={`${card} rounded-2xl p-6`}>
                <div className={`w-12 h-12 ${vc.bg} rounded-xl flex items-center justify-center mb-4`}>
                  <vc.icon className={`w-6 h-6 ${vc.color}`} />
                </div>
                <h3 className={`text-lg font-bold mb-2 ${textP}`} style={{ fontFamily: 'Outfit' }}>{vc.title}</h3>
                <p className={`text-sm leading-relaxed ${textS}`}>{vc.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className={`${card} rounded-3xl p-10 text-center`}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className={`w-16 h-16 ${isDark ? 'bg-white/[0.05]' : 'bg-gray-100'} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                <Users className={`w-8 h-8 ${textM}`} />
              </div>
              <h2 className={`text-3xl font-bold mb-4 ${textP}`} style={{ fontFamily: 'Outfit' }}>{s.teamTitle}</h2>
              <p className={`text-lg max-w-2xl mx-auto leading-relaxed mb-6 ${textS}`}>{s.teamDesc}</p>
              <p className={`text-sm italic ${textM}`}>{s.teamPlaceholder}</p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
